/**
 * Admin Issues Management Page
 * Secure admin interface for issue management
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import adminApiService from '../services/adminApiService';
import MFAVerification from '../components/MFAVerification';

const AdminIssues = () => {
  const { user } = useAuth();
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [issues, setIssues] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: ''
  });
  
  useEffect(() => {
    // Check MFA requirement
    const mfaVerifiedAt = sessionStorage.getItem('mfaVerifiedAt');
    const mfaTimeout = 15 * 60 * 1000; // 15 minutes
    
    if (user?.mfa_required) {
      if (!mfaVerifiedAt || (Date.now() - parseInt(mfaVerifiedAt)) > mfaTimeout) {
        setMfaRequired(true);
        return;
      } else {
        setMfaVerified(true);
      }
    } else {
      setMfaVerified(true);
    }
    
    if (mfaVerified || !user?.mfa_required) {
      loadDashboard();
    }
  }, [user, mfaVerified]);
  
  // Load issues when switching to issues tab
  useEffect(() => {
    if (activeTab === 'issues' && (mfaVerified || !user?.mfa_required)) {
      loadIssues();
    }
  }, [activeTab, mfaVerified, user?.mfa_required]);
  
  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getDashboard();
      setDashboard(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard');
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const loadIssues = async () => {
    try {
      const response = await adminApiService.listIssues(filters);
      setIssues(response.data.issues || []);
    } catch (err) {
      console.error('Issues load error:', err);
      setError(err.response?.data?.error || 'Failed to load issues');
    }
  };
  
  const handleMfaVerified = () => {
    setMfaRequired(false);
    setMfaVerified(true);
    loadDashboard();
  };
  
  const handleMfaCancel = () => {
    window.location.href = '/dashboard';
  };
  
  const handleResolveIssue = async (issueId, resolution) => {
    try {
      await adminApiService.resolveIssue(issueId, resolution);
      await loadDashboard();
      await loadIssues();
      setSelectedIssue(null);
      alert('Issue resolved successfully');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to resolve issue');
    }
  };
  
  const handleAssignIssue = async (issueId, userId) => {
    try {
      await adminApiService.assignIssue(issueId, userId);
      await loadIssues();
      alert('Issue assigned successfully');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to assign issue');
    }
  };
  
  if (mfaRequired && !mfaVerified) {
    return <MFAVerification onVerified={handleMfaVerified} onCancel={handleMfaCancel} />;
  }
  
  if (loading && !dashboard && !error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="bg-red-900 bg-opacity-50 border border-red-700 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-red-400 mb-4">Error Loading Dashboard</h2>
            <p className="text-gray-300 mb-4">{error}</p>
            <button
              onClick={() => {
                setError(null);
                setLoading(true);
                loadDashboard();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      {/* Security Banner */}
      <div className="bg-red-900 bg-opacity-50 border border-red-700 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <div>
              <h3 className="font-bold text-red-300">ADMIN ACCESS - SECURE AREA</h3>
              <p className="text-sm text-red-400">All actions are logged and audited</p>
            </div>
          </div>
          <div className="text-sm text-gray-400">
            Session: {user?.username} | {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-700">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'dashboard'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Dashboard
        </button>
        <button
          onClick={() => setActiveTab('issues')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'issues'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          All Issues
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'analytics'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Analytics
        </button>
      </div>
      
      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Issues</p>
                  <p className="text-3xl font-bold text-white mt-2">{dashboard.summary?.total_issues || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Open Issues</p>
                  <p className="text-3xl font-bold text-yellow-400 mt-2">{dashboard.summary?.open_issues || 0}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Security Issues</p>
                  <p className="text-3xl font-bold text-red-400 mt-2">{dashboard.security?.total_security_issues || 0}</p>
                </div>
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Avg Resolution</p>
                  <p className="text-3xl font-bold text-green-400 mt-2">
                    {dashboard.summary?.avg_resolution_hours 
                      ? `${dashboard.summary.avg_resolution_hours}h`
                      : 'N/A'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          {/* Priority Breakdown */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-xl font-bold mb-4">Priority Breakdown</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-400">{dashboard.priority_breakdown?.critical || 0}</p>
                <p className="text-sm text-gray-400 mt-1">Critical</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-400">{dashboard.priority_breakdown?.high || 0}</p>
                <p className="text-sm text-gray-400 mt-1">High</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-400">{dashboard.priority_breakdown?.medium || 0}</p>
                <p className="text-sm text-gray-400 mt-1">Medium</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-400">{dashboard.priority_breakdown?.low || 0}</p>
                <p className="text-sm text-gray-400 mt-1">Low</p>
              </div>
            </div>
          </div>
          
          {/* Recent Issues */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-xl font-bold mb-4">Recent Issues (Last 24 Hours)</h3>
            <p className="text-gray-400">{dashboard.summary?.recent_issues_24h || 0} new issues</p>
          </div>
        </div>
      )}
      
      {/* Issues Tab */}
      {activeTab === 'issues' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <select
                value={filters.status}
                onChange={async (e) => {
                  const newFilters = { ...filters, status: e.target.value };
                  setFilters(newFilters);
                  try {
                    const response = await adminApiService.listIssues(newFilters);
                    setIssues(response.data.issues || []);
                  } catch (err) {
                    console.error('Filter error:', err);
                  }
                }}
                className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
              >
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              
              <select
                value={filters.priority}
                onChange={async (e) => {
                  const newFilters = { ...filters, priority: e.target.value };
                  setFilters(newFilters);
                  try {
                    const response = await adminApiService.listIssues(newFilters);
                    setIssues(response.data.issues || []);
                  } catch (err) {
                    console.error('Filter error:', err);
                  }
                }}
                className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
              >
                <option value="">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              
              <select
                value={filters.category}
                onChange={async (e) => {
                  const newFilters = { ...filters, category: e.target.value };
                  setFilters(newFilters);
                  try {
                    const response = await adminApiService.listIssues(newFilters);
                    setIssues(response.data.issues || []);
                  } catch (err) {
                    console.error('Filter error:', err);
                  }
                }}
                className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
              >
                <option value="">All Categories</option>
                <option value="security">Security</option>
                <option value="technical">Technical</option>
                <option value="compliance">Compliance</option>
                <option value="user_support">User Support</option>
              </select>
            </div>
          </div>
          
          {/* Issues List */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Assigned</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {issues.map((issue) => (
                    <tr key={issue.id} className="hover:bg-gray-700">
                      <td className="px-6 py-4 text-sm text-gray-300">#{issue.id}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => setSelectedIssue(issue)}
                          className="text-blue-400 hover:text-blue-300 text-left"
                        >
                          {issue.title}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">{issue.category}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          issue.priority === 'critical' ? 'bg-red-900 text-red-300' :
                          issue.priority === 'high' ? 'bg-orange-900 text-orange-300' :
                          issue.priority === 'medium' ? 'bg-yellow-900 text-yellow-300' :
                          'bg-blue-900 text-blue-300'
                        }`}>
                          {issue.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">{issue.status}</td>
                      <td className="px-6 py-4 text-sm text-gray-400">{issue.assigned_to?.username || 'Unassigned'}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => setSelectedIssue(issue)}
                          className="text-blue-400 hover:text-blue-300 text-sm"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {/* Issue Detail Modal */}
      {selectedIssue && (
        <IssueDetailModal
          issue={selectedIssue}
          onClose={() => setSelectedIssue(null)}
          onResolve={handleResolveIssue}
          onAssign={handleAssignIssue}
          onUpdate={(updatedIssue) => setSelectedIssue(updatedIssue)}
        />
      )}
    </div>
  );
};

// Issue Detail Modal Component
const IssueDetailModal = ({ issue, onClose, onResolve, onAssign, onUpdate }) => {
  const [resolution, setResolution] = useState('');
  const [resolutionSteps, setResolutionSteps] = useState(['']);
  const [securityNotes, setSecurityNotes] = useState('');
  const [comment, setComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  
  const handleResolve = () => {
    if (!resolution.trim()) {
      alert('Resolution description is required');
      return;
    }
    
    onResolve(issue.id, {
      resolution,
      resolution_steps: resolutionSteps.filter(s => s.trim()),
      security_notes: securityNotes
    });
  };
  
  const addResolutionStep = () => {
    setResolutionSteps([...resolutionSteps, '']);
  };
  
  const updateResolutionStep = (index, value) => {
    const updated = [...resolutionSteps];
    updated[index] = value;
    setResolutionSteps(updated);
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold">Issue #{issue.id}: {issue.title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Issue Details */}
          <div>
            <h3 className="text-lg font-bold mb-2">Description</h3>
            <p className="text-gray-300 whitespace-pre-wrap">{issue.description}</p>
          </div>
          
          {/* Resolution Form */}
          {issue.status !== 'resolved' && issue.status !== 'closed' && (
            <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
              <h3 className="text-lg font-bold mb-4">Resolve Issue</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Resolution Description *
                  </label>
                  <textarea
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
                    rows={4}
                    placeholder="Describe how the issue was resolved..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Resolution Steps
                  </label>
                  {resolutionSteps.map((step, index) => (
                    <input
                      key={index}
                      type="text"
                      value={step}
                      onChange={(e) => updateResolutionStep(index, e.target.value)}
                      className="w-full px-4 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white mb-2"
                      placeholder={`Step ${index + 1}...`}
                    />
                  ))}
                  <button
                    onClick={addResolutionStep}
                    className="text-blue-400 hover:text-blue-300 text-sm"
                  >
                    + Add Step
                  </button>
                </div>
                
                {issue.category === 'security' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Security Notes (Internal)
                    </label>
                    <textarea
                      value={securityNotes}
                      onChange={(e) => setSecurityNotes(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
                      rows={3}
                      placeholder="Internal security notes (not visible to user)..."
                    />
                  </div>
                )}
                
                <button
                  onClick={handleResolve}
                  className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Resolve Issue
                </button>
              </div>
            </div>
          )}
          
          {/* Add Comment */}
          <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
            <h3 className="text-lg font-bold mb-4">Add Comment</h3>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full px-4 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white mb-2"
              rows={3}
              placeholder="Add a comment..."
            />
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                  className="rounded"
                />
                Internal Note (not visible to user)
              </label>
              <button
                onClick={async () => {
                  try {
                    await adminApiService.addComment(issue.id, comment, isInternal);
                    setComment('');
                    // Reload issue details
                    const response = await adminApiService.getIssueDetails(issue.id);
                    if (onUpdate) {
                      onUpdate(response.data);
                    }
                  } catch (err) {
                    alert(err.response?.data?.error || 'Failed to add comment');
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add Comment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminIssues;
